import React, { useEffect, useState } from "react"
import { Flex, Box } from "@rebass/grid/emotion"
import { TextElementNova } from "@nutrafol/nutrafol-ui-kit/dist/TextElementNova"
import { ButtonNova } from "@nutrafol/nutrafol-ui-kit/dist/ButtonNova"
import { segmentEvent } from "@nutrafol/nutrafol-ui-kit/utils/segmentfunctions"
import { navigate } from "gatsby"

import CoreUpsellRow from "./coreproduct-upsell-row"
import { useIsFirstRender } from "../../../../../../hooks/useIsFirstRender"
import LoadingAnimation from "../../../../../loading/loadinganimation"

import {
  bearerToken,
  envUrl,
  Rows,
  ButtonContent,
  AddedRow,
  DEVMODE,
  COREINFO,
  ERRORMSG,
  ERRORMSG2,
  ERRORMSG3,
  HEADER2,
  LABELVIEW,
  LABELCHECKOUT,
  LABELREMOVE,
  SERUMDESCRIPTION,
  FHKSML_SOURCE,
  HAIRSERUMSML_PROPS,
  FHKSML_PROPS,
  FHKSML_PROPS_MAP,
  DELIVERYOT,
  DELIVERY3,
  DELIVERY3REFILLS,
  SUB3M,
  SUB1M,
  TOTAL,
  TOTALPLAN,
  ATC,
  ATCPLAN,
  MON1,
  MON3,
  HS_SOURCE,
  OT,
} from "./coreproduct-upsell-data"

/*
 *CoreUpsell component for displaying upsell options on the product detail page.
 *
 *@param {Object} props - Component properties.
 *@param {Object} props.upsellOption - The upsell option data.
 *@param {String} variant - VWO variant: [control,a,b]
 *@returns {JSX.Element} The CoreUpsell component.
 *NT2-20055
 *
 *AB Test | 1oz HS upsell on PDPs
 * LS useEffect operates with updated upsellAdded
 */

const CoreUpsell = ({ upsellOption, upsellCoupon, variant }) => {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [upsellAdded, setUpsellAdded] = useState(false)
  const [message, setMessage] = useState(null)
  const isFirstRender = useIsFirstRender()
  // UPSELLSKU is an original Core added to cart
  const UPSELLSKU = upsellOption?.sku
  // Is it 3 MONTHS?
  const IS3MON = ["BAL3-S01"].includes(UPSELLSKU)
  // Is it ONE TIME ADDITION?
  const ONETIMEMODE = IS3MON && variant === "a"
  const HAIRSERUMSML_PROPS_PREPARED = { ...HAIRSERUMSML_PROPS }

  /**
   **
   * PROPS SWAP BASED ON MONTHS
   **
   */
  const PLANINFO = IS3MON ? SUB3M : SUB1M
  const SOURCE = ONETIMEMODE ? HS_SOURCE : FHKSML_SOURCE
  const TERM = IS3MON ? MON3 : MON1
  const PLANTITLE = ONETIMEMODE ? TOTAL : TOTALPLAN
  const LABELADD = ONETIMEMODE ? ATC : ATCPLAN

  if (IS3MON) {
    HAIRSERUMSML_PROPS_PREPARED.price = 124
    HAIRSERUMSML_PROPS_PREPARED.old_price = 147
    HAIRSERUMSML_PROPS_PREPARED.shipping = DELIVERY3
    upsellOption.shipping = DELIVERY3REFILLS
    if (ONETIMEMODE) {
      HAIRSERUMSML_PROPS_PREPARED.price = 49
      HAIRSERUMSML_PROPS_PREPARED.old_price = null
      HAIRSERUMSML_PROPS_PREPARED.purchase_type = OT
      HAIRSERUMSML_PROPS_PREPARED.shipping = DELIVERYOT
      HAIRSERUMSML_PROPS_PREPARED.shippingIcon = false
    }
  }

  upsellOption.info = COREINFO

  /**
   **
   * PREPARING PRODUCTS
   **
   */
  let FHKSML_PROPS_PREPARED = { ...FHKSML_PROPS }

  if (FHKSML_PROPS_MAP[UPSELLSKU]) {
    FHKSML_PROPS_PREPARED = { ...FHKSML_PROPS, ...FHKSML_PROPS_MAP[UPSELLSKU] }
  }

  const COMBOPRICE = IS3MON
    ? upsellOption.price + HAIRSERUMSML_PROPS_PREPARED.price
    : FHKSML_PROPS_PREPARED.price

  const PRICE = upsellAdded ? COMBOPRICE : upsellOption.price

  const UPSELLPRODUCT = ONETIMEMODE
    ? HAIRSERUMSML_PROPS_PREPARED
    : FHKSML_PROPS_PREPARED

  const product = {
    billing_interval: UPSELLPRODUCT.billing_interval,
    image_url: UPSELLPRODUCT.image?.url,
    name: UPSELLPRODUCT.name,
    price: UPSELLPRODUCT.price,
    product_category: UPSELLPRODUCT.product_category,
    product_gender: UPSELLPRODUCT.product_gender,
    purchase_type: UPSELLPRODUCT.purchase_type,
    shipping_interval: UPSELLPRODUCT.shipping_interval,
    sku: UPSELLPRODUCT.sku,
    subscription_type: UPSELLPRODUCT.subscription_type,
  }

  if (DEVMODE) {
    console.log("core upsell upsellOption", upsellOption)
    console.log("core upsell coupon", upsellCoupon)
    console.log(variant, "IS3MON", IS3MON, "ONETIMEMODE", ONETIMEMODE)
    console.log("UPSELLPRODUCT", UPSELLPRODUCT)
  }

  useEffect(() => {
    if (isFirstRender) {
      segmentEvent("Upsell Product Shown", {
        products: [product],
        source: SOURCE,
      })
      return
    }
    segmentEvent(
      upsellAdded ? "Upsell Product Added" : "Upsell Product Removed",
      {
        products: [product],
        source: SOURCE,
      }
    )
  }, [upsellAdded])

  const formatAndSetLocalStorageCart = (ajaxCartResponse) => {
    // console.log("ajaxCartResponse ", ajaxCartResponse)
    if (ajaxCartResponse && typeof ajaxCartResponse === "object") {
      let formattedCart = {
        totals: ajaxCartResponse?.[0],
        items: ajaxCartResponse?.[1],
      }
      localStorage.setItem("cart", JSON.stringify(formattedCart))
    }
  }

  /**
   * Adds the upsell item to the cart.
   *
   * @returns {Promise<void>} A promise that resolves when the item is added to the cart.
   */
  const addItem = (sku) => {
    const body = { skus: sku, purchase_source: SOURCE, code: upsellCoupon }

    fetch(`${envUrl}rest/V1/repack/add_to_cart/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((res) => res.json())
      .then((resJson) => {
        setIsSubmitted(false)
        formatAndSetLocalStorageCart(resJson)
        //LS upsellAdded  updated here
        setUpsellAdded(!upsellAdded)
      })
      .catch((error) => {
        setIsSubmitted(false)
        setMessage(ERRORMSG3)
        console.error("Error:", error)
      })
  }

  /**
   * Handles the click event for adding/removing the upsell item.
   */

  const handleClick = () => {
    if (isSubmitted) {
      return
    }
    setMessage(null)
    setIsSubmitted(true)
    /*
     * for cart development locally
     * https://nutrafol.atlassian.net/wiki/spaces/NS/pages/1787920385/Cart+-+working+locally
     */
    let url = `${envUrl}rest/V1/nutrafol-carts/`
    if (DEVMODE) {
      // url = `${envUrl}rest/V1/nutrafol-carts/rqHRXwk6U2P2yZNKe1GafjQtq6YBa112`
    }

    fetch(url, {
      method: "GET",
    })
      .then((res) => res.json())
      .then(async (resJson) => {
        if (typeof resJson !== "object") {
          const cartData = JSON.parse(resJson)
          if (typeof cartData === "object" && cartData.items) {
            if (ONETIMEMODE && !upsellAdded) {
              //Simply add SERUM in ONETIME MODE
              addItem(HAIRSERUMSML_PROPS_PREPARED.sku)
              return
            }

            // ALL OTHER FLOWS REQUIRE DELETION FROM CART
            const headers = {
              Accept: "application/json",
              "Content-Type": "application/json; charset=UTF-8",
              Authorization: `Bearer ${bearerToken}`,
            }

            //in SUBMODE we swap BUNDLE
            let cartItem = upsellAdded
              ? cartData.items?.[FHKSML_PROPS_PREPARED.sku]
              : cartData.items?.[UPSELLSKU]

            //in ONETIMEMODE we delete SERUM
            if (ONETIMEMODE) {
              cartItem = cartData.items?.[HAIRSERUMSML_PROPS_PREPARED.sku]
            }

            const quote = cartItem?.quote
            const cartId = cartData?.totals?.cart_id

            //console.log("quote", quote)

            if (cartItem && quote && cartId) {
              const url = `${envUrl}rest/V1/carts/${cartId}/items/${quote?.quote_item_id}`
              try {
                if (quote.qty === 1) {
                  await fetch(url, {
                    method: "DELETE",
                    withCredentials: true,
                    headers,
                  })
                } else {
                  let updatedBody = {
                    cartItem: {
                      quoteId: Number(cartId),
                      qty: quote.qty - 1,
                    },
                  }
                  await fetch(url, {
                    method: "PUT",
                    withCredentials: true,
                    headers,
                    body: JSON.stringify(updatedBody),
                  })
                }

                if (ONETIMEMODE) {
                  // IF WE REMOVE SERUM in ONETIME MODE, nothing to add, finish here
                  setIsSubmitted(false)
                  /*
                   * formatAndSetLocalStorageCart(resJson)
                   * LS upsellAdded  updated here
                   */
                  setUpsellAdded(!upsellAdded)
                } else {
                  /*
                   * proceed with SUB MODE -> addItem
                   * upsellAdded here is not updated yet, be careful
                   */
                  addItem(upsellAdded ? UPSELLSKU : FHKSML_PROPS_PREPARED.sku)
                }
              } catch (error) {
                setMessage(ERRORMSG2)
                console.error("delete error: ", error)
              }
            } else {
              console.error("ERROR ADDING TO CART")
              console.info("cartItem", cartItem)
              console.info("quote", quote)
              console.info("cartId", cartId)
              setMessage(ERRORMSG2)
              setIsSubmitted(false)
            }
          } else {
            setMessage(cartData)
            setIsSubmitted(false)
          }
        } else {
          setMessage(ERRORMSG)
          setIsSubmitted(false)
        }
      })
      .catch((error) => {
        setMessage(ERRORMSG)
        setIsSubmitted(false)
        console.warn("Fetch error: ", error)
      })
  }

  /**
   * Handles the checkout process.
   *
   * @param {string} [text=LABELVIEW] - The text for the button.
   * @param {string} [destination="/checkout/cart/"] - The destination URL.
   * @param {boolean} [outside=false] - Whether the navigation is outside the app.
   */

  const handleCheckout = (
    text = LABELVIEW,
    destination = "/checkout/cart/",
    outside = false
  ) => {
    segmentEvent("Marketing CTA Clicked", {
      destination,
      text,
      type: "button",
    })
    setTimeout(() => {
      if (outside) {
        window.location = envUrl + destination
      } else {
        navigate(destination)
      }
    }, 500)
  }

  return (
    <Box>
      {upsellAdded && isSubmitted ? <LoadingAnimation /> : null}
      <Rows px={[0, 0, 0, 16]} py={[0]}>
        <CoreUpsellRow item={upsellOption}></CoreUpsellRow>
        <Box pt={[16]} pb={[24, 24, 16, 24]}>
          {AddedRow}
        </Box>
        <hr />
        <Box pt={[24, 24, 16, 24]} pb={[24, 24, 16, 24]}>
          <TextElementNova
            text={HEADER2}
            className={`Body-20-Dia-Strong text-nova-brand-primary-80`}
          />
        </Box>
        <CoreUpsellRow item={HAIRSERUMSML_PROPS_PREPARED}></CoreUpsellRow>
        <Box py={[16]}>
          <TextElementNova
            text={SERUMDESCRIPTION}
            className={`Body-10-Dia-Normal`}
          />
        </Box>
        {message ? (
          <Box py={[24]}>
            <TextElementNova
              text={message}
              className={`text-nova-functional-error Body-20-Dia-Normal`}
            />
          </Box>
        ) : null}
        {upsellAdded ? (
          <Flex py={[16]} alignItems="center">
            <Box flex={1}>{AddedRow}</Box>
            <Box className={`text-right`}>
              <ButtonNova
                level={"Textlink"}
                onClick={handleClick}
                label={LABELREMOVE}
              />
            </Box>
          </Flex>
        ) : (
          <ButtonNova
            className={`${isSubmitted ? "disabled" : ""}`}
            loading={isSubmitted ? "loading" : null}
            onClick={handleClick}
            label={LABELADD}
            icon={"Right"}
            width={"100%"}
          />
        )}
        <Box pt={[24, 24, 16, 24]} pb={[24, 24, 16, 24]}>
          <hr />
        </Box>
        {ONETIMEMODE ? (
          <Flex width={[1]} pb={[16, 16, 0, 16]}>
            <Box>
              <TextElementNova
                text={PLANTITLE}
                className={`Body-20-Dia-Strong`}
              />
            </Box>
            <Box ml={["auto"]}>
              <TextElementNova
                text={`$${PRICE}`}
                className={`Body-20-Dia-Strong`}
              />
            </Box>
          </Flex>
        ) : (
          <>
            <Flex width={[1]}>
              <Box>
                <TextElementNova
                  text={PLANTITLE}
                  className={`Body-20-Dia-Strong`}
                />
              </Box>
              <Box ml={["auto"]}>
                <TextElementNova
                  text={`$${PRICE}/${TERM}`}
                  className={`Body-20-Dia-Strong`}
                />
              </Box>
            </Flex>
            <Flex width={[1]} pt={["4px"]}>
              <TextElementNova
                text={PLANINFO}
                className={`Body-10-Dia-Normal text-nova-transparency-black-60`}
              />
            </Flex>
          </>
        )}
      </Rows>
      <ButtonContent
        flexWrap="wrap"
        width="100%"
        mt={[24, 24, 16, 32]}
        px={[0, 0, 0, 16]}
      >
        <Box width={1 / 2} pr={["8px"]}>
          <ButtonNova
            level={"Secondary"}
            width={"100%"}
            onClick={() => {
              handleCheckout()
            }}
            label={LABELVIEW}
            className={`${isSubmitted ? "disabled" : ""}`}
          />
        </Box>
        <Box width={1 / 2} pl={["8px"]}>
          <ButtonNova
            icon={"Right"}
            width={"100%"}
            label={LABELCHECKOUT}
            onClick={() => {
              handleCheckout(LABELCHECKOUT, `checkout/index/`, true)
            }}
            className={`${isSubmitted ? "disabled" : ""}`}
          />
        </Box>
      </ButtonContent>
    </Box>
  )
}

export default CoreUpsell
